import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import ApiInspector from '../../../components/ApiInspector';
import ComboTabellata from '../../../components/ComboTabellata';
import ModaleCercaRecord from '../../../components/ModaleCercaRecord';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { showToast } from '../../../components/Toast';
import { apiFetch, apiDownload } from '../../../lib/api';
import { usePermesso } from '../../../context/PermessiContext';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { useBloccoRecordContesto } from '../../../hooks/useBloccoRecordContesto';
import { formatCurrency } from '../../../utils/formatters';
import { API_DOCS } from './DispositivoSchedaPage.api';

const MODULO = 'aziendale.hr.dispositivi';
const LISTA_PATH = '/gestione-aziendale/hr/dispositivi';

// ── Tipi ─────────────────────────────────────────────────────────────────────
type Proprieta = 'PROPRIETA' | 'TERZI';
type Stato = 'NON_ASSEGNATO' | 'ASSEGNATO' | 'DISMESSO';
type Tab = 'anagrafica' | 'assegnazioni' | 'ubicazioni' | 'note';

interface RifTabellato { id: number; nome: string; }
interface RifNominativo { id: number; codice: string; descrizione: string; }

interface Assegnazione {
  id: number;
  dipendente: { id: number; nome: string; cognome: string };
  consegnato: { id: number; nome: string; cognome: string } | null;
  dataAssegnazione: string;
  dataRestituzione: string | null;
  noteRestituzione: string | null;
  note: string | null;
  verbaleFileName: string | null;
}

interface MovimentoUbicazione {
  id: number;
  ubicazioneDa: RifTabellato | null;
  ubicazioneA: RifTabellato;
  dataSpostamento: string;
  note: string | null;
  utenteNome: string;
}

interface Nota {
  id: number;
  testo: string;
  autoreId: number;
  autoreNome: string;
  createdAt: string;
  updatedAt: string;
}

interface Dispositivo {
  id: number;
  societaId: number;
  codiceMatricola: string;
  tipologia: RifTabellato;
  marca: string | null;
  modello: string | null;
  seriale: string | null;
  partNumber: string | null;
  proprieta: Proprieta;
  fornitoreId: number | null;
  fornitore: RifNominativo | null;
  fornitoreDescrizione: string | null;
  dataFatturaAcquisto: string | null;
  numeroFatturaAcquisto: string | null;
  importoAcquisto: number | null;
  causaleTerziId: number | null;
  causaleTerzi: RifTabellato | null;
  proprietarioId: number | null;
  proprietario: RifNominativo | null;
  proprietarioDescrizione: string | null;
  dataRicezione: string | null;
  numeroDdtRicezione: string | null;
  dataDdtRicezione: string | null;
  ubicazioneCorrenteId: number | null;
  ubicazioneCorrente: RifTabellato | null;
  stato: Stato;
  assegnazioneAttiva: { id: number; dipendente: { id: number; nome: string; cognome: string } } | null;
  storicoAssegnazioni: Assegnazione[];
  storicoUbicazioni: MovimentoUbicazione[];
  note: Nota[];
}

interface FormAnagrafica {
  codiceMatricola: string;
  tipologiaId: number | null;
  marca: string;
  modello: string;
  seriale: string;
  partNumber: string;
  proprieta: Proprieta;
  fornitoreId: number | null;
  fornitoreDescrizione: string;
  numeroFatturaAcquisto: string;
  dataFatturaAcquisto: string;
  importoAcquisto: string;
  causaleTerziId: number | null;
  proprietarioId: number | null;
  proprietarioDescrizione: string;
  dataRicezione: string;
  numeroDdtRicezione: string;
  dataDdtRicezione: string;
}

interface DipendenteOption { id: number; nome: string; cognome: string; mansione: string | null; }
interface NominativoOption { id: number; codice: string; descrizione: string; partitaIva: string | null; ruolo: string; }

const STATO_BADGE: Record<Stato, string> = {
  NON_ASSEGNATO: 'bop-badge--neutral',
  ASSEGNATO: 'bop-badge--success',
  DISMESSO: 'bop-badge--danger',
};
const STATO_LABEL: Record<Stato, string> = {
  NON_ASSEGNATO: 'Non assegnato',
  ASSEGNATO: 'Assegnato',
  DISMESSO: 'Dismesso',
};

const fmtDate = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString('it-IT') : '—');
const fmtDateTime = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const soloData = (d: string | null | undefined) => (d ? d.substring(0, 10) : '');
const oggi = () => new Date().toISOString().substring(0, 10);

function formToState(d: Dispositivo): FormAnagrafica {
  return {
    codiceMatricola: d.codiceMatricola,
    tipologiaId: d.tipologia?.id ?? null,
    marca: d.marca || '',
    modello: d.modello || '',
    seriale: d.seriale || '',
    partNumber: d.partNumber || '',
    proprieta: d.proprieta,
    fornitoreId: d.fornitoreId,
    fornitoreDescrizione: d.fornitoreDescrizione || '',
    numeroFatturaAcquisto: d.numeroFatturaAcquisto || '',
    dataFatturaAcquisto: soloData(d.dataFatturaAcquisto),
    importoAcquisto: d.importoAcquisto !== null && d.importoAcquisto !== undefined ? String(d.importoAcquisto) : '',
    causaleTerziId: d.causaleTerziId,
    proprietarioId: d.proprietarioId,
    proprietarioDescrizione: d.proprietarioDescrizione || '',
    dataRicezione: soloData(d.dataRicezione),
    numeroDdtRicezione: d.numeroDdtRicezione || '',
    dataDdtRicezione: soloData(d.dataDdtRicezione),
  };
}

export default function DispositivoSchedaPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = usePermesso();

  const [dispositivo, setDispositivo] = useState<Dispositivo | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [tab, setTab] = useState<Tab>('anagrafica');

  const canEdit = can(MODULO, 'edit', dispositivo?.societaId);
  const canDelete = can(MODULO, 'delete', dispositivo?.societaId);

  // Form anagrafica
  const [form, setForm] = useState<FormAnagrafica | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [saving, setSaving] = useState(false);

  const isDirty = initialSnapshot !== '' && JSON.stringify(form) !== initialSnapshot;
  useDirtyForm(isDirty, `Dispositivo ${dispositivo?.codiceMatricola ?? ''}`.trim());
  useBloccoRecordContesto({
    recordSocietaId: dispositivo?.societaId,
    label: dispositivo ? `Dispositivo ${dispositivo.codiceMatricola}` : 'Dispositivo',
  });

  // Nominativi (fornitore / proprietario)
  const [nominativi, setNominativi] = useState<NominativoOption[]>([]);
  const [cercaNominativo, setCercaNominativo] = useState<null | 'fornitore' | 'proprietario'>(null);
  const [nominativoSel, setNominativoSel] = useState<{ fornitore: RifNominativo | null; proprietario: RifNominativo | null }>({
    fornitore: null, proprietario: null,
  });

  // Assegnazione
  const [showAssegna, setShowAssegna] = useState(false);
  const [dipendenti, setDipendenti] = useState<DipendenteOption[]>([]);
  const [assegnaDipendenteId, setAssegnaDipendenteId] = useState<string>('');
  const [assegnaConsegnatoId, setAssegnaConsegnatoId] = useState<string>('');
  const [assegnaData, setAssegnaData] = useState(oggi());
  const [assegnaNote, setAssegnaNote] = useState('');
  const [assegnaVerbale, setAssegnaVerbale] = useState<File | null>(null);
  const [assegnaError, setAssegnaError] = useState('');
  const [assegnaSaving, setAssegnaSaving] = useState(false);

  // Restituzione
  const [restituzioneTarget, setRestituzioneTarget] = useState<Assegnazione | null>(null);
  const [restituzioneData, setRestituzioneData] = useState(oggi());
  const [restituzioneNote, setRestituzioneNote] = useState('');
  const [restituzioneSaving, setRestituzioneSaving] = useState(false);

  // Spostamento ubicazione
  const [showSposta, setShowSposta] = useState(false);
  const [spostaUbicazioneId, setSpostaUbicazioneId] = useState<number | null>(null);
  const [spostaData, setSpostaData] = useState(oggi());
  const [spostaNote, setSpostaNote] = useState('');
  const [spostaError, setSpostaError] = useState('');
  const [spostaSaving, setSpostaSaving] = useState(false);

  // Note
  const [nuovaNota, setNuovaNota] = useState('');
  const [notaSaving, setNotaSaving] = useState(false);
  const [notaInModifica, setNotaInModifica] = useState<number | null>(null);
  const [notaTestoModifica, setNotaTestoModifica] = useState('');

  // ── Caricamento ────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setErrore('');
    try {
      // @api GET /api/hr/dispositivi-aziendali/:id -> {success:true,data:{id,societaId,codiceMatricola,tipologia,marca,modello,seriale,partNumber,proprieta,fornitore,fornitoreDescrizione,dataFatturaAcquisto,numeroFatturaAcquisto,importoAcquisto,causaleTerzi,proprietario,proprietarioDescrizione,dataRicezione,numeroDdtRicezione,dataDdtRicezione,ubicazioneCorrente,stato,assegnazioneAttiva,storicoAssegnazioni,storicoUbicazioni,note}}
      const res = await apiFetch<{ success: boolean; data: Dispositivo }>(`/api/hr/dispositivi-aziendali/${id}`);
      setDispositivo(res.data);
      const nuovoForm = formToState(res.data);
      setForm(nuovoForm);
      setInitialSnapshot(JSON.stringify(nuovoForm));
      setNominativoSel({ fornitore: res.data.fornitore, proprietario: res.data.proprietario });
    } catch (err: any) {
      setErrore(err.message || 'Errore nel caricamento del dispositivo');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Nominativi e dipendenti: caricati una volta nota la società del record
  useEffect(() => {
    if (!dispositivo) return;
    const sid = dispositivo.societaId;
    (async () => {
      try {
        // @api GET /api/hr/dispositivi-aziendali/nominativi-lista?societaId -> {success:true,data:[{id,codice,descrizione,partitaIva,ruolo}]}
        const res = await apiFetch<{ success: boolean; data: NominativoOption[] }>(
          `/api/hr/dispositivi-aziendali/nominativi-lista?societaId=${sid}`
        );
        setNominativi(res.data);
      } catch { /* il picker resta vuoto, il campo libero resta utilizzabile */ }
      try {
        // @api GET /api/hr/assegnazioni-dispositivi/dipendenti-lista?societaId -> {success:true,data:[{id,nome,cognome,mansione,stato}]}
        const res = await apiFetch<{ success: boolean; data: DipendenteOption[] }>(
          `/api/hr/assegnazioni-dispositivi/dipendenti-lista?societaId=${sid}`
        );
        setDipendenti(res.data);
      } catch { /* la combo dipendenti resta vuota */ }
    })();
  }, [dispositivo]);

  const setCampo = <K extends keyof FormAnagrafica>(campo: K, valore: FormAnagrafica[K]) => {
    setForm((prev) => (prev ? { ...prev, [campo]: valore } : prev));
  };

  // ── Salvataggio anagrafica ─────────────────────────────────────────────────
  const salva = async () => {
    if (!form || !dispositivo) return;
    if (!form.codiceMatricola.trim()) { showToast('Codice matricola è obbligatorio', 'error'); return; }
    if (!form.tipologiaId) { showToast('Tipologia è obbligatoria', 'error'); return; }

    setSaving(true);
    try {
      const body = {
        codiceMatricola: form.codiceMatricola.trim(),
        tipologiaId: form.tipologiaId,
        marca: form.marca || null,
        modello: form.modello || null,
        seriale: form.seriale || null,
        partNumber: form.partNumber || null,
        proprieta: form.proprieta,
        fornitoreId: form.fornitoreId,
        fornitoreDescrizione: form.fornitoreDescrizione || null,
        numeroFatturaAcquisto: form.numeroFatturaAcquisto || null,
        dataFatturaAcquisto: form.dataFatturaAcquisto || null,
        importoAcquisto: form.importoAcquisto === '' ? null : form.importoAcquisto,
        causaleTerziId: form.causaleTerziId,
        proprietarioId: form.proprietarioId,
        proprietarioDescrizione: form.proprietarioDescrizione || null,
        dataRicezione: form.dataRicezione || null,
        numeroDdtRicezione: form.numeroDdtRicezione || null,
        dataDdtRicezione: form.dataDdtRicezione || null,
      };
      // @api PUT /api/hr/dispositivi-aziendali/:id {codiceMatricola,tipologiaId,marca,modello,seriale,partNumber,proprieta,fornitoreId,fornitoreDescrizione,numeroFatturaAcquisto,dataFatturaAcquisto,importoAcquisto,causaleTerziId,proprietarioId,proprietarioDescrizione,dataRicezione,numeroDdtRicezione,dataDdtRicezione} -> {success:true,data:{id}}
      await apiFetch(`/api/hr/dispositivi-aziendali/${dispositivo.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      setInitialSnapshot(JSON.stringify(form));
      showToast('Dispositivo salvato', 'success');
      load();
    } catch (err: any) {
      showToast(err.message || 'Errore nel salvataggio', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Dismetti ───────────────────────────────────────────────────────────────
  const dismetti = async () => {
    if (!dispositivo) return;
    try {
      // @api PATCH /api/hr/dispositivi-aziendali/:id/dismetti -> {success:true,data:{id,stato}}
      await apiFetch(`/api/hr/dispositivi-aziendali/${dispositivo.id}/dismetti`, { method: 'PATCH' });
      showToast(dispositivo.proprieta === 'TERZI' ? 'Dispositivo restituito al proprietario' : 'Dispositivo dismesso', 'success');
      load();
    } catch (err: any) {
      showToast(err.message || 'Errore nel dismettere il dispositivo', 'error');
    }
  };

  // ── Assegnazione ───────────────────────────────────────────────────────────
  const apriAssegna = () => {
    setAssegnaDipendenteId('');
    setAssegnaConsegnatoId('');
    setAssegnaData(oggi());
    setAssegnaNote('');
    setAssegnaVerbale(null);
    setAssegnaError('');
    setShowAssegna(true);
  };

  const confermaAssegna = async () => {
    if (!dispositivo) return;
    if (!assegnaDipendenteId) { setAssegnaError('Seleziona un dipendente'); return; }
    if (!assegnaData) { setAssegnaError('Data assegnazione obbligatoria'); return; }
    setAssegnaSaving(true);
    setAssegnaError('');
    try {
      // @api POST /api/hr/assegnazioni-dispositivi {societaId,dispositivoId,dipendenteId,dataAssegnazione,consegnatoId,note} -> {success:true,data:{id}}
      const res = await apiFetch<{ success: boolean; data: { id: number } }>('/api/hr/assegnazioni-dispositivi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          societaId: dispositivo.societaId,
          dispositivoId: dispositivo.id,
          dipendenteId: parseInt(assegnaDipendenteId, 10),
          dataAssegnazione: assegnaData,
          consegnatoId: assegnaConsegnatoId ? parseInt(assegnaConsegnatoId, 10) : null,
          note: assegnaNote || null,
        }),
      });
      if (assegnaVerbale) {
        const fd = new FormData();
        fd.append('verbale', assegnaVerbale);
        // @api POST /api/hr/assegnazioni-dispositivi/:id/verbale {verbale:file} -> {success:true,data:{verbaleGcsPath,verbaleFileName}}
        await apiFetch(`/api/hr/assegnazioni-dispositivi/${res.data.id}/verbale`, { method: 'POST', body: fd });
      }
      showToast('Dispositivo assegnato', 'success');
      setShowAssegna(false);
      load();
    } catch (err: any) {
      setAssegnaError(err.message || 'Errore nell\'assegnazione');
    } finally {
      setAssegnaSaving(false);
    }
  };

  const apriRestituzione = (a: Assegnazione) => {
    setRestituzioneTarget(a);
    setRestituzioneData(oggi());
    setRestituzioneNote('');
  };

  const confermaRestituzione = async () => {
    if (!restituzioneTarget) return;
    setRestituzioneSaving(true);
    try {
      // @api PATCH /api/hr/assegnazioni-dispositivi/:id/restituzione {dataRestituzione,noteRestituzione} -> {success:true,data:{id}}
      await apiFetch(`/api/hr/assegnazioni-dispositivi/${restituzioneTarget.id}/restituzione`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dataRestituzione: restituzioneData, noteRestituzione: restituzioneNote || null }),
      });
      showToast('Restituzione registrata', 'success');
      setRestituzioneTarget(null);
      load();
    } catch (err: any) {
      showToast(err.message || 'Errore nella restituzione', 'error');
    } finally {
      setRestituzioneSaving(false);
    }
  };

  const scaricaVerbale = async (a: Assegnazione) => {
    try {
      // @api GET /api/hr/assegnazioni-dispositivi/:id/verbale -> (file stream)
      await apiDownload(`/api/hr/assegnazioni-dispositivi/${a.id}/verbale`, a.verbaleFileName || 'verbale.pdf');
    } catch (err: any) {
      showToast(err.message || 'Errore nel download del verbale', 'error');
    }
  };

  // ── Ubicazioni ─────────────────────────────────────────────────────────────
  const apriSposta = () => {
    setSpostaUbicazioneId(null);
    setSpostaData(oggi());
    setSpostaNote('');
    setSpostaError('');
    setShowSposta(true);
  };

  const confermaSposta = async () => {
    if (!dispositivo) return;
    if (!spostaUbicazioneId) { setSpostaError('Seleziona l\'ubicazione di destinazione'); return; }
    if (!spostaData) { setSpostaError('Data spostamento obbligatoria'); return; }
    setSpostaSaving(true);
    setSpostaError('');
    try {
      // @api POST /api/hr/dispositivi-aziendali/:id/ubicazioni {ubicazioneAId,dataSpostamento,note} -> {success:true,data:{id,ubicazioneDa,ubicazioneA,dataSpostamento,note,utenteNome}}
      await apiFetch(`/api/hr/dispositivi-aziendali/${dispositivo.id}/ubicazioni`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ubicazioneAId: spostaUbicazioneId, dataSpostamento: spostaData, note: spostaNote || null }),
      });
      showToast('Spostamento registrato', 'success');
      setShowSposta(false);
      load();
    } catch (err: any) {
      setSpostaError(err.message || 'Errore nella registrazione dello spostamento');
    } finally {
      setSpostaSaving(false);
    }
  };

  const eliminaMovimento = async (movimentoId: number) => {
    if (!dispositivo) return;
    try {
      // @api DELETE /api/hr/dispositivi-aziendali/:id/ubicazioni/:movimentoId -> {success:true}
      await apiFetch(`/api/hr/dispositivi-aziendali/${dispositivo.id}/ubicazioni/${movimentoId}`, { method: 'DELETE' });
      showToast('Spostamento eliminato', 'success');
      load();
    } catch (err: any) {
      showToast(err.message || 'Errore nell\'eliminazione dello spostamento', 'error');
    }
  };

  // ── Note ───────────────────────────────────────────────────────────────────
  const aggiungiNota = async () => {
    if (!dispositivo || !nuovaNota.trim()) return;
    setNotaSaving(true);
    try {
      // @api POST /api/hr/dispositivi-aziendali/:id/note {testo} -> {success:true,data:{id,testo,autoreNome,createdAt}}
      await apiFetch(`/api/hr/dispositivi-aziendali/${dispositivo.id}/note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ testo: nuovaNota.trim() }),
      });
      setNuovaNota('');
      load();
    } catch (err: any) {
      showToast(err.message || 'Errore nell\'inserimento della nota', 'error');
    } finally {
      setNotaSaving(false);
    }
  };

  const salvaModificaNota = async (notaId: number) => {
    if (!dispositivo || !notaTestoModifica.trim()) return;
    try {
      // @api PUT /api/hr/dispositivi-aziendali/:id/note/:notaId {testo} -> {success:true,data:{id,testo}}
      await apiFetch(`/api/hr/dispositivi-aziendali/${dispositivo.id}/note/${notaId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ testo: notaTestoModifica.trim() }),
      });
      setNotaInModifica(null);
      load();
    } catch (err: any) {
      showToast(err.message || 'Errore nella modifica della nota', 'error');
    }
  };

  const eliminaNota = async (notaId: number) => {
    if (!dispositivo) return;
    try {
      // @api DELETE /api/hr/dispositivi-aziendali/:id/note/:notaId -> {success:true}
      await apiFetch(`/api/hr/dispositivi-aziendali/${dispositivo.id}/note/${notaId}`, { method: 'DELETE' });
      load();
    } catch (err: any) {
      showToast(err.message || 'Errore nell\'eliminazione della nota', 'error');
    }
  };

  // ── Nominativo picker ──────────────────────────────────────────────────────
  const selezionaNominativo = (record: NominativoOption) => {
    const rif: RifNominativo = { id: record.id, codice: record.codice, descrizione: record.descrizione };
    if (cercaNominativo === 'fornitore') {
      setCampo('fornitoreId', record.id);
      setNominativoSel((p) => ({ ...p, fornitore: rif }));
    } else if (cercaNominativo === 'proprietario') {
      setCampo('proprietarioId', record.id);
      setNominativoSel((p) => ({ ...p, proprietario: rif }));
    }
    setCercaNominativo(null);
  };

  const assegnazioneAttiva = useMemo(
    () => dispositivo?.storicoAssegnazioni.find((a) => !a.dataRestituzione) || null,
    [dispositivo]
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout>
        <div className="bop-disp-scheda">
          <div className="bop-empty-state"><p className="bop-empty-state__text">Caricamento…</p></div>
        </div>
      </Layout>
    );
  }

  if (errore || !dispositivo || !form) {
    return (
      <Layout>
        <div className="bop-disp-scheda">
          <BopBackLink to={LISTA_PATH} label="Torna ai dispositivi" />
          <div className="bop-card">
            <div className="bop-empty-state">
              <p className="bop-empty-state__text">{errore || 'Dispositivo non trovato'}</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bop-disp-scheda">
        <BopBackLink to={LISTA_PATH} label="Torna ai dispositivi" />

        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2 className="bop-page-title">{dispositivo.codiceMatricola}</h2>
            <div className="bop-disp-scheda__sottotitolo">
              <span className="bop-text-muted">{dispositivo.tipologia?.nome}</span>
              {(dispositivo.marca || dispositivo.modello) && (
                <span className="bop-text-muted">
                  {[dispositivo.marca, dispositivo.modello].filter(Boolean).join(' ')}
                </span>
              )}
              <span className={`bop-badge ${STATO_BADGE[dispositivo.stato]}`}>{STATO_LABEL[dispositivo.stato]}</span>
              <span className={`bop-badge ${dispositivo.proprieta === 'TERZI' ? 'bop-badge--warning' : 'bop-badge--info'}`}>
                {dispositivo.proprieta === 'TERZI' ? 'Di terzi' : 'Di proprietà'}
              </span>
            </div>
          </div>
          <div className="bop-disp-scheda__header-actions">
            {canEdit && dispositivo.stato === 'NON_ASSEGNATO' && (
              <button className="bop-btn bop-btn-secondary" onClick={dismetti}>
                {dispositivo.proprieta === 'TERZI' ? 'Restituisci al proprietario' : 'Dismetti'}
              </button>
            )}
            {canEdit && tab === 'anagrafica' && (
              <button className="bop-btn bop-btn-primary" onClick={salva} disabled={saving || !isDirty}>
                {saving ? 'Salvataggio…' : 'Salva'}
              </button>
            )}
          </div>
        </div>

        <div className="bop-tabs">
          <button className={`bop-tab ${tab === 'anagrafica' ? 'active' : ''}`} onClick={() => setTab('anagrafica')}>
            Anagrafica
          </button>
          <button className={`bop-tab ${tab === 'assegnazioni' ? 'active' : ''}`} onClick={() => setTab('assegnazioni')}>
            Assegnazioni ({dispositivo.storicoAssegnazioni.length})
          </button>
          <button className={`bop-tab ${tab === 'ubicazioni' ? 'active' : ''}`} onClick={() => setTab('ubicazioni')}>
            Ubicazioni ({dispositivo.storicoUbicazioni.length})
          </button>
          <button className={`bop-tab ${tab === 'note' ? 'active' : ''}`} onClick={() => setTab('note')}>
            Note ({dispositivo.note.length})
          </button>
        </div>

        {/* ── TAB ANAGRAFICA ──────────────────────────────────────────────── */}
        {tab === 'anagrafica' && (
          <div className="bop-disp-scheda__sezioni">
            <div className="bop-card">
              <div className="bop-card__header"><h3>Identificazione</h3></div>
              <div className="bop-card__body">
                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Codice Matricola <span className="bop-text-danger">*</span></label>
                    <input className="bop-input" value={form.codiceMatricola} disabled={!canEdit}
                      onChange={(e) => setCampo('codiceMatricola', e.target.value)} />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Tipologia <span className="bop-text-danger">*</span></label>
                    <ComboTabellata
                      label="tipologia"
                      value={form.tipologiaId}
                      onChange={(v) => setCampo('tipologiaId', v)}
                      societaId={dispositivo.societaId}
                      endpoint="/api/hr/tipologie-dispositivo"
                      disabled={!canEdit}
                    />
                  </div>
                </div>
                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Marca</label>
                    <input className="bop-input" value={form.marca} disabled={!canEdit}
                      onChange={(e) => setCampo('marca', e.target.value)} />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Modello</label>
                    <input className="bop-input" value={form.modello} disabled={!canEdit}
                      onChange={(e) => setCampo('modello', e.target.value)} />
                  </div>
                </div>
                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Seriale</label>
                    <input className="bop-input" value={form.seriale} disabled={!canEdit}
                      onChange={(e) => setCampo('seriale', e.target.value)} />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Part number</label>
                    <input className="bop-input" value={form.partNumber} disabled={!canEdit}
                      onChange={(e) => setCampo('partNumber', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            <div className="bop-card">
              <div className="bop-card__header"><h3>Provenienza</h3></div>
              <div className="bop-card__body">
                <div className="bop-form-group">
                  <label className="bop-label">Proprietà</label>
                  <select
                    className="bop-select bop-disp-scheda__select-proprieta"
                    value={form.proprieta}
                    disabled={!canEdit}
                    onChange={(e) => setCampo('proprieta', e.target.value as Proprieta)}
                  >
                    <option value="PROPRIETA">Di proprietà dell'azienda</option>
                    <option value="TERZI">Di terzi (fornitore, cliente, partner)</option>
                  </select>
                </div>

                {form.proprieta === 'PROPRIETA' ? (
                  <>
                    <div className="bop-form-row">
                      <div className="bop-form-group">
                        <label className="bop-label">Fornitore</label>
                        <div className="bop-disp-scheda__picker">
                          <span className="bop-disp-scheda__picker-valore">
                            {nominativoSel.fornitore
                              ? `${nominativoSel.fornitore.codice} — ${nominativoSel.fornitore.descrizione}`
                              : <span className="bop-text-muted">Nessun nominativo selezionato</span>}
                          </span>
                          {canEdit && (
                            <div className="bop-disp-scheda__picker-azioni">
                              <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => setCercaNominativo('fornitore')}>
                                Cerca
                              </button>
                              {nominativoSel.fornitore && (
                                <button
                                  className="bop-btn bop-btn-ghost bop-btn-sm"
                                  onClick={() => { setCampo('fornitoreId', null); setNominativoSel((p) => ({ ...p, fornitore: null })); }}
                                >
                                  Rimuovi
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="bop-form-group">
                        <label className="bop-label">Fornitore (non in anagrafica)</label>
                        <input className="bop-input" value={form.fornitoreDescrizione} disabled={!canEdit}
                          placeholder="Nome fornitore in testo libero…"
                          onChange={(e) => setCampo('fornitoreDescrizione', e.target.value)} />
                      </div>
                    </div>
                    <div className="bop-form-row">
                      <div className="bop-form-group">
                        <label className="bop-label">Numero fattura acquisto</label>
                        <input className="bop-input" value={form.numeroFatturaAcquisto} disabled={!canEdit}
                          onChange={(e) => setCampo('numeroFatturaAcquisto', e.target.value)} />
                      </div>
                      <div className="bop-form-group">
                        <label className="bop-label">Data fattura acquisto</label>
                        <input type="date" className="bop-input bop-disp-scheda__input-data" value={form.dataFatturaAcquisto}
                          disabled={!canEdit} onChange={(e) => setCampo('dataFatturaAcquisto', e.target.value)} />
                      </div>
                    </div>
                    <div className="bop-form-row">
                      <div className="bop-form-group">
                        <label className="bop-label">Importo acquisto (€)</label>
                        <input type="number" step="0.01" className="bop-input bop-disp-scheda__input-importo"
                          value={form.importoAcquisto} disabled={!canEdit}
                          onChange={(e) => setCampo('importoAcquisto', e.target.value)} />
                        {form.importoAcquisto !== '' && Number.isFinite(Number(form.importoAcquisto)) && (
                          <span className="bop-text-muted bop-disp-scheda__hint">
                            {formatCurrency(Number(form.importoAcquisto))}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bop-form-row">
                      <div className="bop-form-group">
                        <label className="bop-label">Causale</label>
                        <ComboTabellata
                          label="causale"
                          value={form.causaleTerziId}
                          onChange={(v) => setCampo('causaleTerziId', v)}
                          societaId={dispositivo.societaId}
                          endpoint="/api/hr/causali-dispositivo-terzi"
                          disabled={!canEdit}
                        />
                      </div>
                      <div className="bop-form-group">
                        <label className="bop-label">Data di ricezione</label>
                        <input type="date" className="bop-input bop-disp-scheda__input-data" value={form.dataRicezione}
                          disabled={!canEdit} onChange={(e) => setCampo('dataRicezione', e.target.value)} />
                      </div>
                    </div>
                    <div className="bop-form-row">
                      <div className="bop-form-group">
                        <label className="bop-label">Azienda proprietaria</label>
                        <div className="bop-disp-scheda__picker">
                          <span className="bop-disp-scheda__picker-valore">
                            {nominativoSel.proprietario
                              ? `${nominativoSel.proprietario.codice} — ${nominativoSel.proprietario.descrizione}`
                              : <span className="bop-text-muted">Nessun nominativo selezionato</span>}
                          </span>
                          {canEdit && (
                            <div className="bop-disp-scheda__picker-azioni">
                              <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => setCercaNominativo('proprietario')}>
                                Cerca
                              </button>
                              {nominativoSel.proprietario && (
                                <button
                                  className="bop-btn bop-btn-ghost bop-btn-sm"
                                  onClick={() => { setCampo('proprietarioId', null); setNominativoSel((p) => ({ ...p, proprietario: null })); }}
                                >
                                  Rimuovi
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="bop-form-group">
                        <label className="bop-label">Azienda proprietaria (non in anagrafica)</label>
                        <input className="bop-input" value={form.proprietarioDescrizione} disabled={!canEdit}
                          placeholder="Nome azienda in testo libero…"
                          onChange={(e) => setCampo('proprietarioDescrizione', e.target.value)} />
                      </div>
                    </div>
                    <div className="bop-form-row">
                      <div className="bop-form-group">
                        <label className="bop-label">Numero DDT</label>
                        <input className="bop-input" value={form.numeroDdtRicezione} disabled={!canEdit}
                          onChange={(e) => setCampo('numeroDdtRicezione', e.target.value)} />
                      </div>
                      <div className="bop-form-group">
                        <label className="bop-label">Data DDT</label>
                        <input type="date" className="bop-input bop-disp-scheda__input-data" value={form.dataDdtRicezione}
                          disabled={!canEdit} onChange={(e) => setCampo('dataDdtRicezione', e.target.value)} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── TAB ASSEGNAZIONI ────────────────────────────────────────────── */}
        {tab === 'assegnazioni' && (
          <div className="bop-card">
            <div className="bop-card__header">
              <h3>Storico assegnazioni</h3>
              {canEdit && dispositivo.stato === 'NON_ASSEGNATO' && (
                <button className="bop-btn bop-btn-primary bop-btn-sm" onClick={apriAssegna}>Assegna a dipendente</button>
              )}
              {canEdit && assegnazioneAttiva && (
                <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => apriRestituzione(assegnazioneAttiva)}>
                  Registra restituzione
                </button>
              )}
            </div>
            <div className="bop-card__body">
              {dispositivo.storicoAssegnazioni.length === 0 ? (
                <div className="bop-empty-state"><p className="bop-empty-state__text">Nessuna assegnazione registrata</p></div>
              ) : (
                <div className="bop-table-wrapper">
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>Dipendente</th>
                        <th>Consegnato da</th>
                        <th>Dal</th>
                        <th>Al</th>
                        <th>Note</th>
                        <th>Note restituzione</th>
                        <th>Verbale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dispositivo.storicoAssegnazioni.map((a) => (
                        <tr key={a.id}>
                          <td>{a.dipendente.cognome} {a.dipendente.nome}</td>
                          <td className="bop-text-muted">{a.consegnato ? `${a.consegnato.cognome} ${a.consegnato.nome}` : '—'}</td>
                          <td>{fmtDate(a.dataAssegnazione)}</td>
                          <td>
                            {a.dataRestituzione
                              ? fmtDate(a.dataRestituzione)
                              : <span className="bop-badge bop-badge--success">Attiva</span>}
                          </td>
                          <td className="bop-text-muted bop-disp-scheda__td-note">{a.note || '—'}</td>
                          <td className="bop-text-muted bop-disp-scheda__td-note">{a.noteRestituzione || '—'}</td>
                          <td>
                            {a.verbaleFileName ? (
                              <button className="bop-btn bop-btn-ghost bop-btn-sm" onClick={() => scaricaVerbale(a)}>
                                Scarica PDF
                              </button>
                            ) : <span className="bop-text-muted">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TAB UBICAZIONI ──────────────────────────────────────────────── */}
        {tab === 'ubicazioni' && (
          <div className="bop-card">
            <div className="bop-card__header">
              <h3>
                Ubicazione corrente:{' '}
                {dispositivo.ubicazioneCorrente
                  ? dispositivo.ubicazioneCorrente.nome
                  : <span className="bop-text-muted">non indicata</span>}
              </h3>
              {canEdit && (
                <button className="bop-btn bop-btn-primary bop-btn-sm" onClick={apriSposta}>Sposta</button>
              )}
            </div>
            <div className="bop-card__body">
              {dispositivo.storicoUbicazioni.length === 0 ? (
                <div className="bop-empty-state"><p className="bop-empty-state__text">Nessuno spostamento registrato</p></div>
              ) : (
                <div className="bop-table-wrapper">
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Da</th>
                        <th>A</th>
                        <th>Note</th>
                        <th>Registrato da</th>
                        <th className="bop-table__th--actions">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dispositivo.storicoUbicazioni.map((m) => (
                        <tr key={m.id}>
                          <td>{fmtDate(m.dataSpostamento)}</td>
                          <td className="bop-text-muted">{m.ubicazioneDa?.nome || '—'}</td>
                          <td>{m.ubicazioneA?.nome}</td>
                          <td className="bop-text-muted bop-disp-scheda__td-note">{m.note || '—'}</td>
                          <td className="bop-text-muted">{m.utenteNome}</td>
                          <td className="bop-table__td--actions">
                            <BopRowActions actions={[
                              {
                                icon: <RowIcon.Trash />, label: 'Elimina',
                                onClick: () => eliminaMovimento(m.id), variant: 'danger',
                                hidden: !canDelete,
                              },
                            ]} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TAB NOTE ────────────────────────────────────────────────────── */}
        {tab === 'note' && (
          <div className="bop-card">
            <div className="bop-card__header"><h3>Note e commenti</h3></div>
            <div className="bop-card__body">
              {canEdit && (
                <div className="bop-disp-scheda__nota-nuova">
                  <textarea
                    className="bop-input bop-disp-scheda__textarea"
                    rows={3}
                    placeholder="Scrivi una nota…"
                    value={nuovaNota}
                    onChange={(e) => setNuovaNota(e.target.value)}
                  />
                  <button className="bop-btn bop-btn-primary" onClick={aggiungiNota} disabled={notaSaving || !nuovaNota.trim()}>
                    {notaSaving ? 'Salvataggio…' : 'Aggiungi nota'}
                  </button>
                </div>
              )}

              {dispositivo.note.length === 0 ? (
                <div className="bop-empty-state"><p className="bop-empty-state__text">Nessuna nota registrata</p></div>
              ) : (
                <ul className="bop-disp-scheda__timeline">
                  {dispositivo.note.map((n) => (
                    <li key={n.id} className="bop-disp-scheda__timeline-item">
                      <span className="bop-disp-scheda__timeline-dot" />
                      <div className="bop-disp-scheda__timeline-content">
                        <div className="bop-disp-scheda__timeline-header">
                          <span className="bop-disp-scheda__timeline-autore">{n.autoreNome}</span>
                          <span className="bop-text-muted bop-disp-scheda__timeline-data">
                            {fmtDateTime(n.createdAt)}
                            {n.updatedAt !== n.createdAt ? ' (modificata)' : ''}
                          </span>
                          {canEdit && (
                            <div className="bop-disp-scheda__timeline-azioni">
                              <button
                                className="bop-btn bop-btn-ghost bop-btn-sm"
                                onClick={() => { setNotaInModifica(n.id); setNotaTestoModifica(n.testo); }}
                              >
                                Modifica
                              </button>
                              {canDelete && (
                                <button className="bop-btn bop-btn-ghost bop-btn-sm" onClick={() => eliminaNota(n.id)}>
                                  Elimina
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        {notaInModifica === n.id ? (
                          <div className="bop-disp-scheda__nota-modifica">
                            <textarea
                              className="bop-input bop-disp-scheda__textarea"
                              rows={3}
                              value={notaTestoModifica}
                              onChange={(e) => setNotaTestoModifica(e.target.value)}
                            />
                            <div className="bop-disp-scheda__nota-modifica-azioni">
                              <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => setNotaInModifica(null)}>
                                Annulla
                              </button>
                              <button className="bop-btn bop-btn-primary bop-btn-sm" onClick={() => salvaModificaNota(n.id)}>
                                Salva
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="bop-disp-scheda__timeline-testo">{n.testo}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* ── Modale ricerca nominativo ───────────────────────────────────── */}
        {cercaNominativo && (
          <ModaleCercaRecord
            titolo={cercaNominativo === 'fornitore' ? 'Cerca fornitore' : 'Cerca azienda proprietaria'}
            colonne={[
              { key: 'codice', label: 'Codice', mono: true },
              { key: 'descrizione', label: 'Denominazione' },
              { key: 'partitaIva', label: 'P. IVA', mono: true },
              { key: 'ruolo', label: 'Ruolo' },
            ]}
            dati={nominativi}
            campoRicerca={['codice', 'descrizione', 'partitaIva']}
            onSeleziona={selezionaNominativo}
            onChiudi={() => setCercaNominativo(null)}
          />
        )}

        {/* ── Modale assegnazione ─────────────────────────────────────────── */}
        {showAssegna && (
          <div className="bop-modal-overlay">
            <div className="bop-modal bop-disp-scheda__modal">
              <div className="bop-modal__header">
                <h3 className="bop-modal__title">Assegna dispositivo — {dispositivo.codiceMatricola}</h3>
              </div>
              <div className="bop-modal__body">
                {assegnaError && <div className="bop-inline-error">{assegnaError}</div>}
                <div className="bop-form-group">
                  <label className="bop-label">Dipendente <span className="bop-text-danger">*</span></label>
                  <select className="bop-select" value={assegnaDipendenteId} onChange={(e) => setAssegnaDipendenteId(e.target.value)}>
                    <option value="">Seleziona dipendente…</option>
                    {dipendenti.map((d) => (
                      <option key={d.id} value={d.id}>{d.cognome} {d.nome}{d.mansione ? ` — ${d.mansione}` : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Consegnato da</label>
                  <select className="bop-select" value={assegnaConsegnatoId} onChange={(e) => setAssegnaConsegnatoId(e.target.value)}>
                    <option value="">— nessuno —</option>
                    {dipendenti.map((d) => (
                      <option key={d.id} value={d.id}>{d.cognome} {d.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Data assegnazione <span className="bop-text-danger">*</span></label>
                  <input type="date" className="bop-input bop-disp-scheda__input-data" value={assegnaData}
                    onChange={(e) => setAssegnaData(e.target.value)} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Note</label>
                  <textarea className="bop-input bop-disp-scheda__textarea" rows={3} value={assegnaNote}
                    onChange={(e) => setAssegnaNote(e.target.value)} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Verbale di consegna (PDF)</label>
                  <input type="file" accept=".pdf" className="bop-input"
                    onChange={(e) => setAssegnaVerbale(e.target.files?.[0] || null)} />
                </div>
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setShowAssegna(false)} disabled={assegnaSaving}>
                  Annulla
                </button>
                <button className="bop-btn bop-btn-primary" onClick={confermaAssegna} disabled={assegnaSaving}>
                  {assegnaSaving ? 'Assegnazione…' : 'Assegna'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modale restituzione ─────────────────────────────────────────── */}
        {restituzioneTarget && (
          <div className="bop-modal-overlay">
            <div className="bop-modal bop-disp-scheda__modal">
              <div className="bop-modal__header">
                <h3 className="bop-modal__title">
                  Restituzione — {restituzioneTarget.dipendente.cognome} {restituzioneTarget.dipendente.nome}
                </h3>
              </div>
              <div className="bop-modal__body">
                <div className="bop-form-group">
                  <label className="bop-label">Data restituzione <span className="bop-text-danger">*</span></label>
                  <input type="date" className="bop-input bop-disp-scheda__input-data" value={restituzioneData}
                    onChange={(e) => setRestituzioneData(e.target.value)} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Note restituzione</label>
                  <textarea className="bop-input bop-disp-scheda__textarea" rows={3} value={restituzioneNote}
                    onChange={(e) => setRestituzioneNote(e.target.value)} />
                </div>
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setRestituzioneTarget(null)} disabled={restituzioneSaving}>
                  Annulla
                </button>
                <button className="bop-btn bop-btn-primary" onClick={confermaRestituzione} disabled={restituzioneSaving}>
                  {restituzioneSaving ? 'Salvataggio…' : 'Registra restituzione'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modale spostamento ubicazione ───────────────────────────────── */}
        {showSposta && (
          <div className="bop-modal-overlay">
            <div className="bop-modal bop-disp-scheda__modal">
              <div className="bop-modal__header">
                <h3 className="bop-modal__title">Sposta dispositivo — {dispositivo.codiceMatricola}</h3>
              </div>
              <div className="bop-modal__body">
                {spostaError && <div className="bop-inline-error">{spostaError}</div>}
                <div className="bop-form-group">
                  <label className="bop-label">Ubicazione attuale</label>
                  <p className="bop-disp-scheda__readonly">{dispositivo.ubicazioneCorrente?.nome || 'non indicata'}</p>
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Nuova ubicazione <span className="bop-text-danger">*</span></label>
                  <ComboTabellata
                    label="ubicazione"
                    value={spostaUbicazioneId}
                    onChange={setSpostaUbicazioneId}
                    societaId={dispositivo.societaId}
                    endpoint="/api/hr/ubicazioni-dispositivo"
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Data spostamento <span className="bop-text-danger">*</span></label>
                  <input type="date" className="bop-input bop-disp-scheda__input-data" value={spostaData}
                    onChange={(e) => setSpostaData(e.target.value)} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Note</label>
                  <textarea className="bop-input bop-disp-scheda__textarea" rows={3} value={spostaNote}
                    onChange={(e) => setSpostaNote(e.target.value)} />
                </div>
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn bop-btn-secondary" onClick={() => setShowSposta(false)} disabled={spostaSaving}>
                  Annulla
                </button>
                <button className="bop-btn bop-btn-primary" onClick={confermaSposta} disabled={spostaSaving}>
                  {spostaSaving ? 'Salvataggio…' : 'Registra spostamento'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
